// Thin client for the PHP backend (api/index.php). Manages the session cookie
// (sent automatically with credentials) and the CSRF token required for
// state-changing requests.

/** API endpoint relative to the game page — never use a domain-root path like /api/. */
function apiUrl(query: string): string {
  return new URL(`api/index.php?${query}`, document.baseURI).href;
}

let csrf = '';
let online = true;

export function isOnline(): boolean {
  return online;
}

interface ApiResult {
  ok: boolean;
  [k: string]: unknown;
}

async function parseApiResponse(res: Response): Promise<ApiResult> {
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiResult;
  } catch {
    throw new Error('Server returned invalid response (not JSON). Check API URL / PHP setup.');
  }
}

async function ensureCsrf(): Promise<void> {
  if (csrf) return;
  const res = await fetch(apiUrl('action=csrf'), { credentials: 'include' });
  const data = await parseApiResponse(res);
  if (typeof data.csrf === 'string') csrf = data.csrf;
}

async function call(action: string, method: 'GET' | 'POST', body?: unknown): Promise<ApiResult> {
  const headers: Record<string, string> = {};
  if (method === 'POST') {
    await ensureCsrf();
    headers['Content-Type'] = 'application/json';
    headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(apiUrl(`action=${action}`), {
    method,
    credentials: 'include',
    headers,
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });
  const data = await parseApiResponse(res);
  if (typeof data.csrf === 'string') csrf = data.csrf;
  if (!data.ok) throw new Error((data.error as string) || `HTTP ${res.status}`);
  return data;
}

export interface SaveSlotInfo {
  slot: number;
  updated_at: number;
  size: number;
}

// Returns the current username (or null) and refreshes the CSRF token.
export async function fetchMe(): Promise<string | null> {
  try {
    const d = await call('me', 'GET');
    online = true;
    return (d.user as string | null) ?? null;
  } catch {
    online = false;
    return null;
  }
}

export async function register(username: string, password: string): Promise<string> {
  const d = await call('register', 'POST', { username, password });
  return d.user as string;
}

export async function login(username: string, password: string): Promise<string> {
  const d = await call('login', 'POST', { username, password });
  return d.user as string;
}

export async function logout(): Promise<void> {
  await call('logout', 'POST');
  csrf = '';
}

export async function listSaves(): Promise<SaveSlotInfo[]> {
  const d = await call('saves', 'GET');
  return (d.slots as SaveSlotInfo[]) ?? [];
}

export async function loadSlot(slot: number): Promise<string> {
  const d = await call(`load&slot=${slot}`, 'GET');
  return d.data as string;
}

export async function saveSlot(slot: number, data: string): Promise<void> {
  await call('save', 'POST', { slot, data });
}

export async function deleteSlot(slot: number): Promise<void> {
  await call('delete_save', 'POST', { slot });
}

export interface UserSettings {
  reticle: boolean;
  inrange: boolean;
  bgm_ambient: boolean;
  bgm_battle: boolean;
}

export async function fetchSettings(): Promise<UserSettings | null> {
  const d = await call('settings', 'GET');
  const s = d.settings;
  if (s === null || s === undefined) return null;
  return s as UserSettings;
}

export async function saveSettings(settings: UserSettings): Promise<void> {
  await call('settings', 'POST', { settings });
}
